import { DrillDownValuesAndDomains, RiskReportResults } from 'app/area/case-report/risk-listing/types-store';
import { useInstanceKey, useInstanceOptions } from 'app/area/favourite/favourite-context';
import { debounceExecuteFavouriteWait } from 'app/constants';
import { FavouriteDefinition } from 'app/types-shared';
import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { actionCreators as riskMatrixListingActionCreators } from './actions';
import { useActionCreators as useFavouriteActionCreators } from 'app/area/favourite/hooks';
import {
  selectDrillDownReportForSelectedReport, selectLayoutAttributesForSelectedReport,
  selectLayoutTypeForSelectedReport
} from 'app/area/favourite/selectors';
import { getFavouriteLayoutAttributes } from 'app/components/favourite-layout';
import { AddInstance, ClearInstance } from 'app/area/case-report/risk-listing/action-types';
import {
  selecOptionsDrillDownTo,
  selectErrorsExecuteFailed, selectFetching, selectFetchingOptionsDrillDownTo, selectLayoutTypeAttributes,
  selectRiskReportResults
} from 'app/area/case-report/risk-listing/selectors';
import { codeItemToStoreValue } from 'app/infrastructure/data/codes';
import { ComplexValue } from 'ui';
import { ReportCommonApi } from 'app/area/reports/types-api';
import { api as riskReportApi } from 'app/area/case-report/risk-listing/api';

export function useInstanceSlice() {
  const dispatch = useDispatch();
  const instanceKey = useInstanceKey();

  useEffect(
    () => {
      dispatch(new AddInstance(instanceKey));
      return () => {
        dispatch(new ClearInstance(instanceKey));
      };
    },
    [instanceKey]
  );
}

export function useRiskMatrixListingEvents(
  workingDefinition: FavouriteDefinition,
  autoExecute: boolean,
  layoutTypeContextId: number,
  results?: RiskReportResults,
  reportApiOverrides?: Partial<ReportCommonApi>
) {
  const instanceKey = useInstanceKey();

  const {
    executionCounter
  } = useInstanceOptions();

  const finalApi = useMemo(() => ({
    ...riskReportApi,
    ...(!!reportApiOverrides && {
      ...reportApiOverrides,
      executeRiskReport: reportApiOverrides?.executeFavourite
    })
  }), [riskReportApi, reportApiOverrides]);

  const {
    executeRiskReport,
    drilldown,
    loadOptionsDrillDownTo: onLoadOptionsDrillDownTo,
    setDrillDownReport: setWorkingDefinitionDrillDownReport
  } = useActionCreators(finalApi);

  const {
    setWorkingDefinitionLayoutAttributes
  } = useFavouriteActionCreators();

  const debouncedExecute = useCallback(debounce((definition) => {
    executeRiskReport(definition, layoutTypeContextId);
  }, debounceExecuteFavouriteWait), [instanceKey]);

  const execute = useCallback(() => {
    debouncedExecute(workingDefinition);
  }, [workingDefinition, results, debouncedExecute, instanceKey]);

  const layoutType = useSelector(selectLayoutTypeForSelectedReport(instanceKey));

  const onDrilldown = useCallback((definition: FavouriteDefinition, drillDownValuesAndDomains: DrillDownValuesAndDomains) => {
    drilldown(definition, { autoExecute: true, filters: drillDownValuesAndDomains });
  }, [drilldown, instanceKey]);

  const onLoadOptionsDrillDown = useCallback(() => {
    onLoadOptionsDrillDownTo();
  }, [onLoadOptionsDrillDownTo, instanceKey]);

  const onSetDrillDownReport = useCallback((drillDownReport: ComplexValue<number>) => {
    setWorkingDefinitionDrillDownReport(drillDownReport);
  }, [setWorkingDefinitionDrillDownReport, instanceKey]);

  const onUpdateLayoutAttributes = useCallback((attributes: { [key: string]: string | boolean | number }[]) => {
    setWorkingDefinitionLayoutAttributes(attributes, layoutType.v, layoutTypeContextId);
  }, [setWorkingDefinitionLayoutAttributes, instanceKey, layoutTypeContextId, layoutType.v]);

  useEffect(() => {
    autoExecute && execute();
  }, [autoExecute, workingDefinition, instanceKey, executionCounter]);

  return {
    onDrilldown,
    onUpdateLayoutAttributes,
    onLoadOptionsDrillDown,
    onSetDrillDownReport
  };
}

export function useFavouriteLayoutAttributes(
  layoutTypeContextId: number
) {
  const instanceKey = useInstanceKey();
  const layoutType = useSelector(selectLayoutTypeForSelectedReport(instanceKey));
  const layoutAttributes = useSelector(selectLayoutAttributesForSelectedReport(instanceKey));
  const layoutTypeAttributes = useSelector(selectLayoutTypeAttributes(instanceKey));

  return useMemo(() => {

    const favouriteLayoutAttributes = {
      ...getFavouriteLayoutAttributes(
        layoutAttributes,
        layoutType.v,
        layoutTypeContextId
      )
    };

    // Merge layout type attributes from the configuration of the specif layout type with attributes selected by the user
    // in the attributes selection area in the risk report. This allows to override Highcharts options from the administration app.
    return {
      layoutAttributes: { ...layoutTypeAttributes, ...favouriteLayoutAttributes }
    };
  }, [layoutTypeContextId, layoutType, layoutAttributes, layoutTypeAttributes]);
}

export function useActionCreators(api) {
  const dispatch = useDispatch();
  const instanceKey = useInstanceKey();
  return useMemo(() => bindActionCreators(riskMatrixListingActionCreators(api, instanceKey), dispatch), [instanceKey]);
}

export function useSelectors() {
  const instanceKey = useInstanceKey();
  const fetching = useSelector(selectFetching(instanceKey));
  const riskReportResults = useSelector(selectRiskReportResults(instanceKey));
  const errorsExecuteFailed = useSelector(selectErrorsExecuteFailed(instanceKey));
  const fetchingOptionsDrillDownTo = useSelector(selectFetchingOptionsDrillDownTo(instanceKey));
  const optionsDrillDownTo = useSelector(selecOptionsDrillDownTo(instanceKey));

  return {
    fetching,
    riskReportResults,
    errorsExecuteFailed,
    fetchingOptionsDrillDownTo,
    optionsDrillDownTo
  };
}

export function useSelectedDrillDownReport() {
  const instanceKey = useInstanceKey();
  const drillDownReport = useSelector(selectDrillDownReportForSelectedReport(instanceKey));
  return codeItemToStoreValue(drillDownReport);
}
